import type { AppState, RedirectLoginOptions } from '@auth0/auth0-react';

import type { CartUI } from '@libs/common-node';
import { getCountry, getSourceIdForCountry, isLastActiveExpired } from '@libs/core';

import { persistAuth0ReturnTo, resetAuth0CartMergeSignal } from './auth0CartMergeSync';
import { persistAuth0LoginMethod } from './auth0LoginAnalyticsSignal';
import { hasAutoshipInCart } from './hasFulfillmentMethodInCart';
import { cartHasVetDietItem, cartHasVetRxItem } from './vetRx';

export const AUTH0_SOURCE_ID_PARAM = 'ext-sourceId';
export const AUTH0_AUTOSHIP_PARAM = 'ext-autoship';
export const AUTH0_RX_FOOD_PARAM = 'ext-rx-food';
export const AUTH0_RX_MED_PARAM = 'ext-rx-med';
export const AUTH0_EXT_FLAG_VALUE = 'true';

export type Auth0AppState = AppState & {
    /** Header button path — `returnTo` is the current page, not checkout / wishlist / offers. */
    returnToIsImplicit?: boolean;
};

type Auth0LoginRedirectOptions = {
    returnTo: string;
    authorizationParams?: Record<string, string>;
    /**
     * Replace the current history entry instead of pushing a new one.
     * Required on auto-redirect pages (`/sign-in`, `/create-account`) so Safari
     * cannot bounce back to them instead of `/auth/callback`.
     */
    replaceHistory?: boolean;
    /** Guest cart used to derive cart-category `ext-*` flags. Omit for logged-in flows. */
    cart?: CartUI | null;
    returnToIsImplicit?: boolean;
};

/**
 * Used by `loginWithRedirect({ openUrl })` so the authorize navigation does not
 * leave the interstitial page in history (`location.assign` is the SDK default).
 */
export function openAuth0AuthorizeUrl(url: string) {
    globalThis.location.replace(url);
}

export function getAuth0SourceIdAuthorizationParam(): Record<typeof AUTH0_SOURCE_ID_PARAM, string> {
    return {
        [AUTH0_SOURCE_ID_PARAM]: String(getSourceIdForCountry(getCountry())),
    };
}

/** Guest-cart category flags for Auth0 Actions. Only matching flags are sent. */
export function getAuth0CartCategoryAuthorizationParams(cart: CartUI | null = null): Record<string, string> {
    const params: Record<string, string> = {};

    if (hasAutoshipInCart(cart)) {
        params[AUTH0_AUTOSHIP_PARAM] = AUTH0_EXT_FLAG_VALUE;
    }

    if (cartHasVetDietItem(cart)) {
        params[AUTH0_RX_FOOD_PARAM] = AUTH0_EXT_FLAG_VALUE;
    }

    if (cartHasVetRxItem(cart)) {
        params[AUTH0_RX_MED_PARAM] = AUTH0_EXT_FLAG_VALUE;
    }

    return params;
}

export function getAuth0LoginRedirectOptions({
    returnTo,
    authorizationParams,
    replaceHistory,
    cart,
    returnToIsImplicit,
}: Auth0LoginRedirectOptions): RedirectLoginOptions {
    persistAuth0ReturnTo(returnTo);
    persistAuth0LoginMethod();
    resetAuth0CartMergeSignal();

    return {
        appState: { returnTo, ...(returnToIsImplicit ? { returnToIsImplicit: true } : {}) } satisfies Auth0AppState,
        authorizationParams: {
            ...getAuth0SourceIdAuthorizationParam(),
            ...authorizationParams,
            ...getAuth0CartCategoryAuthorizationParams(cart),
            // Inactivity wins over caller `prompt` so a later top-level authorize cannot
            // silently restore SSO after a public-page background logout.
            ...(isLastActiveExpired() ? { prompt: 'login' as const } : {}),
        },
        ...(replaceHistory ? { openUrl: openAuth0AuthorizeUrl } : {}),
    };
}
